import React, { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import { buildCountryOptions } from "@features/port-security/components/utils";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import type { Nationality } from "@services/gatepass/types";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { stringToTitleCase } from "@utils/formatters/strings";
import isValueFilled from "@utils/isValueFilled";
import { t } from "i18next";
import { FormProvider, useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

const SocialStatus: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { options: nationalities } = useFormattedReference<Nationality>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );
  const { options: countries } = useFormattedReference<{
    countryName: string;
    countryCode: string;
  }>(REFERENCE_DATA_TYPES.COUNTRIES, "bff-gatepass");
  const nationalitiesOptions = useMemo(
    () =>
      nationalities
        .map((n) => ({ label: n.nationality, value: n.nationality }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [nationalities],
  );
  const countriesOptions = useMemo(
    () => buildCountryOptions(countries, { valueMode: "code", formatName: stringToTitleCase }),
    [countries],
  );

  const [
    spouseName,
    spouseNationality,
    spouseBirthPlace,
    spouseBirthDate,
    spouseOccupation,
    spouseWorkPlace,
  ] = useWatch({
    control: formData.control,
    name: [
      "spouseName",
      "spouseNationality",
      "spouseBirthPlace",
      "spouseBirthDate",
      "spouseOccupation",
      "spouseWorkPlace",
    ],
  });

  // If any Social Status field is filled, all Social Status fields become mandatory
  const socialStatusRequired = [
    spouseName,
    spouseNationality,
    spouseBirthPlace,
    spouseBirthDate,
    spouseOccupation,
    spouseWorkPlace,
  ].some(isValueFilled);

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.social-status")}>
        <FormItem<PortSecurityFormData, "spouseName">
          required={socialStatusRequired}
          name="spouseName"
          type="text"
          maxChars={200}
          label={t("security-clearance.spouse-name")}
          testId="security-spouseName"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "spouseNationality">
          required={socialStatusRequired}
          name="spouseNationality"
          type="autocomplete"
          options={nationalitiesOptions}
          label={t("security-clearance.nationality")}
          testId="security-spouseNationality"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "spouseBirthPlace">
          required={socialStatusRequired}
          name="spouseBirthPlace"
          type="autocomplete"
          options={countriesOptions}
          label={t("security-clearance.place-of-birth")}
          testId="security-spouseBirthPlace"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "spouseBirthDate">
          required={socialStatusRequired}
          name="spouseBirthDate"
          type="date-picker"
          label={t("security-clearance.date-of-birth")}
          testId="security-spouseBirthDate"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="past"
        />
        <FormItem<PortSecurityFormData, "spouseOccupation">
          required={socialStatusRequired}
          name="spouseOccupation"
          type="text"
          maxChars={200}
          label={t("security-clearance.occupation")}
          testId="security-spouseOccupation"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "spouseWorkPlace">
          required={socialStatusRequired}
          name="spouseWorkPlace"
          type="text"
          maxChars={200}
          label={t("security-clearance.work-place")}
          testId="security-spouseWorkPlace"
          onFieldChange={updateFormData}
        />
      </FormSection>
    </FormProvider>
  );
};

export default SocialStatus;
